import type { StoragePort } from '../ports/storage';
import { parseStations } from './library';
import { migrate, SCHEMA_VERSION, type StoredSnapshot } from './migrations';
import { DEFAULT_SETTINGS, parseSettings, type Settings } from './settings';
import type { Station } from './stations';

/**
 * Nalaganje in shranjevanje knjižnice (T2 §3, §4, §9).
 *
 * Domena sme poznati `StoragePort`, ker je port pod njo (T1 §2). Ne pozna
 * pa `localStorage` — kar je razlika, zaradi katere je celoten postopek
 * nalaganja, skupaj z migracijami in seedom, testabilen brez brskalnika.
 */

export interface LibraryLoadFailure {
  from: number;
  failedAt: number;
  cause: unknown;
  /** Surovi podatki, da jih vmesnik lahko ponudi v izvoz (T2 §4, pravilo 4). */
  raw: StoredSnapshot;
}

export interface LibrarySnapshot {
  stations: Station[];
  settings: Settings;
  /** Ali je bil ta zagon prvi — vmesnik lahko pove, od kod so postaje. */
  seeded: boolean;
  /**
   * Migracija ni uspela. **Ob tem se v shrambo ne zapiše nič**: delno
   * migriran zapis je slabši od starega, ker ga ne zna prebrati nihče.
   */
  failure: LibraryLoadFailure | null;
}

/**
 * Prvi zagon prepoznamo po **odsotnosti ključa postaj**, ne po praznem
 * seznamu. Prazen seznam je namreč legitimno stanje: uporabnik je seed
 * izbrisal. Če bi bila zanj zamenljiva, bi se seed ob vsakem zagonu vračal
 * (T2 §9).
 */
export async function loadLibrary(
  storage: StoragePort,
  seed: readonly Station[],
): Promise<LibrarySnapshot> {
  const [storedVersion, rawStations, rawSettings] = await Promise.all([
    storage.readVersion(),
    storage.load<unknown>('stations'),
    storage.load<unknown>('settings'),
  ]);

  const firstRun = rawStations === null && rawSettings === null;
  if (firstRun) {
    const snapshot: LibrarySnapshot = {
      stations: seed.map((station) => ({ ...station })),
      settings: { ...DEFAULT_SETTINGS },
      seeded: seed.length > 0,
      failure: null,
    };
    await storage.writeVersion(SCHEMA_VERSION);
    await storage.save('stations', snapshot.stations);
    await storage.save('settings', snapshot.settings);
    return snapshot;
  }

  // Podatki so, verzije ni → predpostavi 1 (T2 §4, pravilo 1).
  const from = storedVersion ?? 1;
  const raw: StoredSnapshot = { stations: rawStations, settings: rawSettings };

  if (from === SCHEMA_VERSION) return readSnapshot(raw);

  // Varnostna kopija pred migracijo, obdržana do naslednje uspešne seje
  // (T2 §4, pravilo 3). Zapiše se PRED migracijo — po njej bi bila kopija
  // tistega, kar smo pravkar morda pokvarili.
  await storage.backup(from, raw);

  const result = migrate(raw, from);
  if (!result.ok) {
    return {
      stations: [],
      settings: { ...DEFAULT_SETTINGS },
      seeded: false,
      failure: { from: result.from, failedAt: result.failedAt, cause: result.cause, raw },
    };
  }

  const snapshot = readSnapshot(result.data);
  await storage.writeVersion(SCHEMA_VERSION);
  await storage.save('stations', snapshot.stations);
  await storage.save('settings', snapshot.settings);
  return snapshot;
}

function readSnapshot(raw: StoredSnapshot): LibrarySnapshot {
  return {
    stations: parseStations(raw.stations),
    settings: parseSettings(raw.settings),
    seeded: false,
    failure: null,
  };
}

export function saveStations(storage: StoragePort, stations: readonly Station[]): Promise<void> {
  return storage.save('stations', stations);
}

export function saveSettings(storage: StoragePort, settings: Settings): Promise<void> {
  return storage.save('settings', settings);
}

/**
 * Obstojnost shrambe (T2 §8).
 *
 * Kliče se **po prvi uporabnikovi investiciji** — prvi priljubljeni ali
 * prvi ročno dodani postaji — ne ob prvem izrisu: takrat uporabnik še nima
 * česa izgubiti in poziv je vsiljiv.
 *
 * `false` **ni napaka.** Chrome tiho odobri ali zavrne, Firefox vpraša.
 * Prava varovalka ostaja izvoz.
 */
export async function requestPersistence(): Promise<boolean> {
  try {
    if (navigator.storage?.persist === undefined) return false;
    if (await navigator.storage.persisted()) return true;
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}
